/**
 * React Context for Authentication
 * Provides useAuth hook for easy access to auth functionality
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthManager, getAuthManager } from './AuthManager';
import { fetchAppConfig } from '../app-config';
import { getCurrentUser } from '../api-client';
import type { UserContext, EmailCredentials } from './types';

export type AuthMode = 'email' | 'oidc' | 'external' | 'none';

interface AuthContextValue {
    authManager: AuthManager;
    isAuthenticated: boolean;
    user: UserContext | null;
    isLoading: boolean;
    isInitialized: boolean;
    signIn: (credentials?: EmailCredentials) => Promise<void>;
    signOut: () => Promise<void>;
    refreshUser: () => Promise<void>;
    getAuthMode: () => AuthMode;
}

export interface ExternalAuthConfig {
    isAuthenticated: boolean;
    user?: UserContext | null;
    isLoading?: boolean;
    signIn?: (credentials?: EmailCredentials) => Promise<void> | void;
    signOut?: () => Promise<void> | void;
    refreshUser?: () => Promise<void> | void;
    getAuthMode?: () => AuthMode;
    getAccessToken?: () => Promise<string | null> | string | null;
    refreshAccessToken?: () => Promise<string | null> | string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
    children: ReactNode;
    externalAuth?: ExternalAuthConfig;
}

const ExternalAuthProvider: React.FC<AuthProviderProps & { externalAuth: ExternalAuthConfig }> = ({ children, externalAuth }) => {
    const [authManager] = useState(() => getAuthManager());

    authManager.useExternalAuth(externalAuth);

    // The host passes user.id = token `sub`, but owner/admin checks (prejoin waiting
    // room) and LiveKit identity expect the internal room-chat user UUID. Resolve it
    // via /api/v1/user/me and override `id`, mirroring the built-in OIDC provider.
    const [resolvedUser, setResolvedUser] = useState<UserContext | null>(externalAuth.user ?? null);

    useEffect(() => {
        if (!externalAuth.isAuthenticated || !externalAuth.user) {
            setResolvedUser(externalAuth.user ?? null);
            return;
        }

        // Optimistically keep the host-provided user until /me resolves.
        setResolvedUser(externalAuth.user);

        let cancelled = false;
        (async () => {
            try {
                const me = await getCurrentUser();
                if (cancelled) return;
                setResolvedUser(prev => ({
                    ...(prev ?? externalAuth.user!),
                    id: me.id,
                    photoUrl: me.photo_url || prev?.photoUrl || externalAuth.user?.photoUrl,
                }));
            } catch (error) {
                // Keep the host-provided user (id = sub) as a fallback.
                console.warn('ExternalAuthProvider: failed to resolve internal user id via /me', error);
            }
        })();

        return () => {
            cancelled = true;
        };
        // Re-resolve on login / user switch.
    }, [externalAuth.isAuthenticated, externalAuth.user?.id, externalAuth.user?.email]);

    const value: AuthContextValue = {
        authManager,
        isAuthenticated: externalAuth.isAuthenticated,
        user: resolvedUser,
        isLoading: externalAuth.isLoading || false,
        isInitialized: true,
        signIn: async (credentials?: EmailCredentials) => {
            await externalAuth.signIn?.(credentials);
        },
        signOut: async () => {
            await externalAuth.signOut?.();
        },
        refreshUser: async () => {
            await externalAuth.refreshUser?.();
        },
        getAuthMode: () => externalAuth.getAuthMode?.() || 'external'
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const ManagedAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [authManager] = useState(() => getAuthManager());
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<UserContext | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    // Initialize auth manager
    useEffect(() => {
        const init = async () => {
            try {
                setIsLoading(true);
                const config = await fetchAppConfig();
                await authManager.initialize(config);
                setIsInitialized(true);

                // Check authentication status after initialization
                const authenticated = await authManager.isAuthenticated();
                setIsAuthenticated(authenticated);

                if (authenticated) {
                    const currentUser = await authManager.getCurrentUser();
                    setUser(currentUser);
                }
            } catch (error) {
                console.error('Failed to initialize auth:', error);
            } finally {
                setIsLoading(false);
            }
        };

        init();
    }, [authManager]);

    // Re-check authentication when window regains focus (e.g., after OIDC callback redirect)
    useEffect(() => {
        if (!isInitialized) return;

        const handleFocus = async () => {
            try {
                const authenticated = await authManager.isAuthenticated();

                // Avoid unnecessary state updates if auth status hasn't changed
                if (authenticated !== isAuthenticated) {
                    setIsAuthenticated(authenticated);

                    if (authenticated) {
                        const currentUser = await authManager.getCurrentUser();
                        setUser(currentUser);
                    } else {
                        setUser(null);
                    }
                }
            } catch (error) {
                console.error('Failed to check auth on focus:', error);
            }
        };

        const handleAuthStateChange = async () => {
            try {
                const authenticated = await authManager.isAuthenticated();
                setIsAuthenticated(authenticated);

                if (authenticated) {
                    const currentUser = await authManager.getCurrentUser();
                    setUser(currentUser);
                } else {
                    setUser(null);
                }
            } catch (error) {
                console.error('Failed to check auth on state change:', error);
            }
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('auth-state-changed', handleAuthStateChange);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('auth-state-changed', handleAuthStateChange);
        };
    }, [authManager, isInitialized, isAuthenticated]);

    const signIn = React.useCallback(
        async (credentials?: EmailCredentials) => {
            try {
                setIsLoading(true);
                await authManager.signIn(credentials);

                const authenticated = await authManager.isAuthenticated();
                setIsAuthenticated(authenticated);

                if (authenticated) {
                    const currentUser = await authManager.getCurrentUser();
                    setUser(currentUser);
                }
            } finally {
                setIsLoading(false);
            }
        },
        [authManager]
    );

    const signOut = React.useCallback(async () => {
        try {
            setIsLoading(true);
            await authManager.signOut();
            setIsAuthenticated(false);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, [authManager]);

    const refreshUser = React.useCallback(async () => {
        try {
            const currentUser = await authManager.getCurrentUser();
            setUser(currentUser);
            setIsAuthenticated(currentUser !== null);
        } catch (error) {
            console.error('Failed to refresh user:', error);
        }
    }, [authManager]);

    const getAuthMode = React.useCallback(() => authManager.getAuthMode(), [authManager]);

    const value: AuthContextValue = {
        authManager,
        isAuthenticated,
        user,
        isLoading,
        isInitialized,
        signIn,
        signOut,
        refreshUser,
        getAuthMode
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, externalAuth }) => {
    if (externalAuth) {
        return <ExternalAuthProvider externalAuth={externalAuth}>{children}</ExternalAuthProvider>;
    }

    return <ManagedAuthProvider>{children}</ManagedAuthProvider>;
};

/**
 * Hook to access authentication context
 * Must be used within AuthProvider
 */
export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
